import React from 'react';

const DriverDashboard = () => {
  return (
    <div className="bg-background text-on-background min-h-screen flex flex-col overflow-hidden font-sans">
      
      {/* 1. Top App Bar */}
      <header className="bg-white border-b border-slate-200/50 shadow-sm sticky top-0 z-50">
        <div className="flex justify-between items-center w-full px-4 sm:px-6 h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full overflow-hidden border-2 border-primary/20">
              <img 
                alt="Driver Profile" 
                className="w-full h-full object-cover" 
                src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100" 
              />
            </div>
            <h1 className="text-base sm:text-lg font-bold tracking-tighter text-slate-900">SmartGaz Driver</h1>
          </div>
          <button className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center rounded-full hover:bg-slate-50 active:scale-95 transition-transform duration-200 text-slate-900">
            <span className="material-symbols-outlined">notifications</span>
          </button>
        </div>
      </header>

      {/* 2. Main Content (Map & Overlay UI) */}
      <main className="relative flex-grow w-full overflow-hidden bg-surface-container">
        
        {/* Map Background */}
        <div className="absolute inset-0 z-0">
          <img 
            className="w-full h-full object-cover grayscale-[0.2] brightness-[0.9]" 
            alt="City Map" 
            src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1000" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 pointer-events-none"></div>
        </div>

        {/* Floating Map Actions */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
          <button className="bg-surface shadow-md p-2 sm:p-3 rounded-xl active:scale-95 transition-all text-on-surface">
            <span className="material-symbols-outlined">my_location</span>
          </button>
          <button className="bg-surface shadow-md p-2 sm:p-3 rounded-xl active:scale-95 transition-all text-on-surface">
            <span className="material-symbols-outlined">layers</span>
          </button>
        </div>

        {/* Active Stop Highlight Card */}
        <div className="absolute top-4 left-4 right-16 sm:right-20 z-10">
          <div className="bg-surface shadow-lg rounded-2xl p-3 sm:p-4 border border-outline/30 backdrop-blur-md bg-white/95">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="bg-primary/10 p-2 sm:p-3 rounded-xl">
                <span className="material-symbols-outlined text-primary text-lg sm:text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                  local_gas_station
                </span>
              </div>
              <div className="flex-grow">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold tracking-widest text-primary uppercase">Current Stop</span>
                  <span className="text-[10px] font-semibold text-secondary px-2 py-0.5 bg-secondary-container rounded-full">0.8 miles</span>
                </div>
                <h2 className="text-on-surface font-bold text-base sm:text-lg leading-tight mt-1">Industrial Gas Depot</h2>
                <p className="text-on-surface-variant text-xs mt-1">482 Tech Valley Parkway, Ste 200</p>
              </div>
            </div>
          </div>
        </div>

        {/* Route Visualization (Horizontal List) */}
        <div className="absolute bottom-[160px] sm:bottom-[180px] left-4 right-4 z-10 flex flex-col gap-2">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            <div className="bg-primary text-white px-3 sm:px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap shadow-md">
              <span className="text-xs font-bold">1</span>
              <span className="text-xs font-medium">Depot</span>
            </div>
            <div className="w-6 sm:w-8 h-[2px] bg-primary/30 shrink-0"></div>
            <div className="bg-white/90 text-on-surface px-3 sm:px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap border border-outline/50 shadow-sm">
              <span className="text-xs font-bold text-primary">2</span>
              <span className="text-xs font-medium">North Residence</span>
            </div>
            <div className="w-6 sm:w-8 h-[2px] bg-outline shrink-0"></div>
            <div className="bg-white/90 text-on-surface px-3 sm:px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap border border-outline/50 shadow-sm opacity-60">
              <span className="text-xs font-bold">3</span>
              <span className="text-xs font-medium">City Hospital</span>
            </div>
          </div>
        </div>

        {/* Sticky Interaction Zone */}
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className="bg-white p-4 sm:p-6 rounded-[2rem] shadow-2xl border border-outline/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-secondary text-base sm:text-lg">schedule</span>
                <span className="text-on-surface font-semibold text-sm">ETA: 14 mins</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-secondary text-base sm:text-lg">route</span>
                <span className="text-on-surface font-semibold text-sm">4.2 km total</span>
              </div>
            </div>
            <button className="w-full bg-primary text-white py-4 sm:py-5 rounded-2xl flex items-center justify-center gap-3 active:scale-[0.97] transition-all duration-200 shadow-[0_8px_30px_rgb(30,159,107,0.3)] group">
              <span className="font-bold text-base sm:text-lg tracking-tight">Next Delivery</span>
              <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>
          </div>
        </div>
      </main>

      {/* 3. Bottom Navigation Bar */}
      <nav className="sticky bottom-0 left-0 w-full z-50 flex justify-around items-center px-4 sm:px-6 py-3 bg-white border-t border-slate-200/50 shadow-lg">
        <NavItem icon="dashboard" label="Dashboard" />
        <NavItem icon="local_shipping" label="Deliveries" active />
        <NavItem icon="qr_code_scanner" label="Scan" />
        <NavItem icon="query_stats" label="Stats" />
      </nav>
    </div>
  );
};

// Yordamchi komponent - Navigatsiya tugmalari uchun
const NavItem = ({ icon, label, active = false }) => (
  <button className={`flex flex-col items-center justify-center px-4 py-1 active:scale-95 transition-all duration-200 ${
    active ? 'bg-emerald-50 text-emerald-700 rounded-xl' : 'text-slate-500 hover:text-emerald-600'
  }`}>
    <span className="material-symbols-outlined" style={active ? { fontVariationSettings: "'FILL' 1" } : {}}>
      {icon}
    </span>
    <span className="text-[12px] font-medium tracking-wide">{label}</span>
  </button>
);

export default DriverDashboard;